<template>
  <TableRow :to="buildingRoute(building.id)">
    <TableCell>
      <IconTile icon="building-06" size="small" color="neutral" />
      <TableCell stacked>
        <Text variant="label-medium">{{ building.name }}</Text>
        <Text variant="body-small" color="secondary">{{ buildingAddressLine(building.location) }}</Text>
      </TableCell>
    </TableCell>

    <TableCell class="units">
      <Text as="span" variant="body-medium" :color="building.unitCount ? 'primary' : 'tertiary'">
        {{ building.unitCount ? pluralize('unit', building.unitCount, true) : 'No units yet' }}
      </Text>
      <Badge
        v-for="split in statusSplit"
        :key="split.status"
        :label="split.label"
        :variant="split.variant"
        size="small"
        dot
      />
    </TableCell>

    <TableCell v-if="showManager">
      <template v-if="building.manager">
        <Avatar :user-name="managerName" show-single-letter size="small" />
        <Text as="span" variant="body-medium">{{ managerLabel }}</Text>
      </template>
      <Text v-else as="span" variant="body-medium" color="tertiary">—</Text>
    </TableCell>

    <TableCell v-if="showPortfolio">
      <PortfolioChip
        v-if="building.portfolio"
        :portfolio="building.portfolio"
        @select="emit('select-portfolio', $event)"
      />
      <Text v-else as="span" variant="body-medium" color="tertiary">—</Text>
    </TableCell>

    <TableCell>
      <Text as="span" variant="body-medium" color="secondary" class="date">{{ createdAgo }}</Text>
    </TableCell>
  </TableRow>
</template>

<script setup lang="ts">
import { useTimeAgo } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import pluralize from 'pluralize';
import { computed } from 'vue';

import { Avatar, Badge, IconTile, TableCell, TableRow, Text } from '@tallo/design-system';
import { teamMemberName } from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';
import { buildingRoute } from '@tallo/investor/navigation';
import { PropertyStatus } from '@tallo/property';

import { BuildingDto } from '../dtos/building.dto.interface';
import { buildingAddressLine } from '../utils/building-address.util';
import { propertyStatusShortLabel, propertyStatusVariant } from '../utils/property-status.util';
import { propertyManagerLabel } from '../utils/property-summary.util';
import PortfolioChip from './portfolio-chip.vue';

const props = defineProps<{
  building: BuildingDto;
  showPortfolio?: boolean;
  showManager?: boolean;
}>();
const emit = defineEmits<{ 'select-portfolio': [portfolioId: string] }>();

const { investor } = storeToRefs(useInvestorStore());

const managerName = computed(() => (props.building.manager ? teamMemberName(props.building.manager) : ''));
const managerLabel = computed(() => propertyManagerLabel(props.building, investor.value?.id));

/** "1 listed", "4 rented out": the units by status, in the statuses' own order */
const statusSplit = computed(() =>
  Object.values(PropertyStatus)
    .filter((status) => props.building.unitsByStatus[status])
    .map((status) => ({
      status,
      label: `${props.building.unitsByStatus[status]} ${propertyStatusShortLabel(status)}`,
      variant: propertyStatusVariant(status),
    })),
);

const createdAgo = useTimeAgo(computed(() => props.building.createdAt));
</script>

<style scoped lang="scss">
// A date is short and reads as one thing; the wider columns give way before it wraps
.date {
  white-space: nowrap;
}

.units {
  flex-wrap: wrap;
}
</style>
